import {
  AudioSource,
  AudioSourceConfig,
  AudioSourceInitResult,
} from './audio-source';

const IDEAL_AUDIO_SAMPLE_RATE = 48_000;

export interface BlobAudioSourceConfig extends AudioSourceConfig {
  mimeType: string;
}

/**
 * Blob Audio Source - Captures pre-encoded audio using MediaRecorder
 * For use as fallback on devices without AudioEncoder support (older iOS)
 */
export class BlobAudioSource extends AudioSource {
  private mediaRecorder: MediaRecorder | null = null;
  private mimeType: string;

  constructor(config: BlobAudioSourceConfig) {
    super(config);
    this.mimeType = config.mimeType;
  }

  getMode(): 'blob' {
    return 'blob';
  }

  async initialize(): Promise<AudioSourceInitResult> {
    this.logger('info', 'Initializing Blob audio source (MediaRecorder mode)', {
      mimeType: this.mimeType,
    });

    // Get actual sample rate from media stream
    const actualSampleRate =
      this.mediaStream.getAudioTracks()[0]?.getSettings().sampleRate ??
      IDEAL_AUDIO_SAMPLE_RATE;

    // Create MediaRecorder
    this.mediaRecorder = new MediaRecorder(this.mediaStream, {
      mimeType: this.mimeType,
    });

    // Set up event handlers
    this.mediaRecorder.addEventListener(
      'dataavailable',
      this.handleDataAvailable,
    );
    this.mediaRecorder.addEventListener('error', this.handleError);
    this.mediaRecorder.addEventListener('start', () => {
      this.logger('info', 'MediaRecorder started successfully (blob mode)', {
        deviceId: this.deviceId,
        audioBitsPerSecond: this.mediaRecorder?.audioBitsPerSecond,
        mimeType: this.mimeType,
      });
    });

    // Start recording with 100ms timeslice
    this.mediaRecorder.start(100);

    // Set up mute detection (pipeline creates its own AudioContext)
    this.setupMuteDetection();

    // Attach track lifecycle listeners (ended + log-only mute/unmute)
    this.setupTrackEventListeners();

    return {
      sampleRate: actualSampleRate,
      channels: this.channelCount as 1 | 2,
    };
  }

  async stop(): Promise<void> {
    this.logger('info', 'Stopping Blob audio source');

    // Stop MediaRecorder
    if (this.mediaRecorder) {
      try {
        this.mediaRecorder.stop();
        this.mediaRecorder.removeEventListener(
          'dataavailable',
          this.handleDataAvailable,
        );
        this.mediaRecorder.removeEventListener('error', this.handleError);
        this.mediaRecorder = null;
      } catch (e) {
        this.logger('warn', 'Error stopping MediaRecorder', {
          error: e,
        });
      }
    }

    // Clean up mute detection
    await this.cleanupMuteDetection();

    // Detach track lifecycle listeners
    this.teardownTrackEventListeners();

    this.logger('info', 'Blob audio source stopped');
  }

  /**
   * Handle data available from MediaRecorder
   */
  private handleDataAvailable = (event: BlobEvent): void => {
    // MediaRecorder can emit zero-byte blobs on startup or during silence —
    // skip them to avoid sending empty data to the worker and STT provider
    if (event.data.size === 0) return;

    // Send blob directly to worker via structured clone
    // No need to convert to ArrayBuffer since we're keeping the blob anyway for audioPacketSubject
    this.postMessage({
      type: 'encoded-audio',
      data: event.data,
      timestamp: event.timeStamp,
    });

    // Also forward to audioPacketSubject for compatibility
    this.audioPacketSubject.next({
      timestamp: event.timeStamp,
      data: event.data,
    });

    // Report bytes received to parent actor
    this.onBytesReceived(event.data.size);
  };

  /**
   * Handle MediaRecorder errors
   */
  private handleError = (event: Event): void => {
    this.logger('warn', 'MediaRecorder error occurred', {
      event,
    });
  };
}
